import React, { useMemo, useState } from 'react';
import { Quote } from '../../types';
import { Button } from '../Button';
import { Bell, Check, Clock, FolderOpen, Building2, History, CalendarClock, Search } from 'lucide-react';
import { quoteAmountSuffix } from '../../services/quoteLogic';
import {
  FollowUpDueState, followUpDueState, followUpLogOf, followUpOutcomeLabel, lastFollowUp,
} from '../../services/quoteFollowUp';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtEur = (n: number) => eur.format(Number.isFinite(n) ? n : 0);
const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');
const fmtDateTime = (v?: string) =>
  v ? new Date(v).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const STATE_META: Record<Exclude<FollowUpDueState, 'none'>, { label: string; cls: string }> = {
  overdue: { label: 'Überfällig', cls: 'bg-red-100 text-red-700' },
  today: { label: 'Heute fällig', cls: 'bg-amber-100 text-amber-700' },
  upcoming: { label: 'Terminiert', cls: 'bg-slate-100 text-slate-600' },
};

interface Props {
  quotes: Quote[];
  todayIso: string;
  customerName: (q: Quote) => string;
  onDone: (q: Quote) => void;
  onSnooze: (id: string, newDate: string) => void;
  onOpen: (q: Quote) => void;
}

/**
 * Reiter "Nachfassen": ein eigener Arbeitsplatz statt der frueheren Kachel
 * ueber der Angebotsliste. Zeigt drei Ebenen — was jetzt faellig ist, was
 * terminiert ist, und was bisher besprochen wurde.
 *
 * Der Bestand wird aus den ohnehin geladenen Angeboten abgeleitet und NICHT
 * getrennt vom Server geholt: sonst zeigen Kachel, Reiter und Liste drei
 * verschiedene Staende, sobald ein Nachfassen erfasst wurde.
 */
export const QuoteFollowUpTab: React.FC<Props> = ({ quotes, todayIso, customerName, onDone, onSnooze, onOpen }) => {
  const [snoozeId, setSnoozeId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showHistory, setShowHistory] = useState(true);

  const matches = (q: Quote) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (q.quoteNumber || '').toLowerCase().includes(s)
      || (customerName(q) || '').toLowerCase().includes(s)
      || (q.title || '').toLowerCase().includes(s);
  };

  const { due, upcoming } = useMemo(() => {
    const due: Quote[] = [];
    const upcoming: Quote[] = [];
    for (const q of quotes) {
      if (!matches(q)) continue;
      const state = followUpDueState(q, todayIso);
      if (state === 'overdue' || state === 'today') due.push(q);
      else if (state === 'upcoming') upcoming.push(q);
    }
    const byDate = (a: Quote, b: Quote) => (a.followUpDate || '').localeCompare(b.followUpDate || '');
    return { due: due.sort(byDate), upcoming: upcoming.sort(byDate) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, todayIso, search]);

  // Verlauf ueber alle Angebote, neuester Eintrag zuerst.
  const history = useMemo(() => {
    const rows: { quote: Quote; entry: ReturnType<typeof followUpLogOf>[number] }[] = [];
    for (const q of quotes) {
      if (!matches(q)) continue;
      for (const entry of followUpLogOf(q)) rows.push({ quote: q, entry });
    }
    return rows.sort((a, b) => (b.entry.at || '').localeCompare(a.entry.at || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, search]);

  const Row: React.FC<{ q: Quote }> = ({ q }) => {
    const state = followUpDueState(q, todayIso);
    const meta = state === 'none' ? null : STATE_META[state];
    const last = lastFollowUp(q);
    const round = followUpLogOf(q).length + 1;
    return (
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-6 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-800">{q.quoteNumber}</span>
            {meta && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>}
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{round}. Nachfassen</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-0.5">
            <Building2 className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <span className="truncate">{customerName(q)}</span>
            <span className="text-slate-300">·</span>
            <span className="font-bold text-slate-700 whitespace-nowrap">
              {fmtEur(q.netTotal)}
              {quoteAmountSuffix(q) && <span className="ml-1 text-[11px] font-medium text-slate-400">{quoteAmountSuffix(q)}</span>}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400 mt-1 flex-wrap">
            <span>Versendet: {fmtDate(q.sentAt)}</span>
            <span className="flex items-center gap-1 text-amber-600 font-medium"><Clock className="w-3 h-3" />Nachfassen: {fmtDate(q.followUpDate)}</span>
          </div>
          {last && (
            <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg px-3 py-1.5">
              <span className="font-bold text-slate-600">Zuletzt {fmtDate(last.at)} · {last.byName} · {followUpOutcomeLabel(last.outcome)}:</span>{' '}
              {last.note}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {snoozeId === q.id ? (
            <input
              type="date"
              autoFocus
              defaultValue={q.followUpDate?.slice(0, 10) || todayIso}
              onChange={(e) => { onSnooze(q.id, e.target.value); setSnoozeId(null); }}
              onBlur={() => setSnoozeId(null)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          ) : (
            <Button variant="secondary" onClick={() => setSnoozeId(q.id)} icon={<Clock className="w-3.5 h-3.5" />} className="h-8 text-xs">Verschieben</Button>
          )}
          <Button variant="secondary" onClick={() => onDone(q)} icon={<Check className="w-3.5 h-3.5" />} className="h-8 text-xs">Erledigt</Button>
          <Button variant="primary" onClick={() => onOpen(q)} icon={<FolderOpen className="w-3.5 h-3.5" />} className="h-8 text-xs">Öffnen</Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div data-glass="filterleiste" className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen: Nummer, Kunde, Bezeichnung…"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
          />
        </div>
        <button
          onClick={() => setShowHistory((v) => !v)}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${showHistory ? 'bg-slate-900 text-white shadow' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
        >
          Verlauf {showHistory ? 'ausblenden' : 'einblenden'}
        </button>
      </div>

      {/* Fällig */}
      <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 bg-amber-50 border-b border-amber-200">
          <Bell className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-bold text-amber-800">Jetzt nachfassen</span>
          <span className="text-xs text-amber-600">({due.length})</span>
        </div>
        {due.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-slate-400">Nichts fällig. {upcoming.length > 0 ? 'Die nächsten Termine stehen unten.' : ''}</p>
        ) : (
          <div className="divide-y divide-slate-100">{due.map((q) => <Row key={q.id} q={q} />)}</div>
        )}
      </div>

      {/* Terminiert */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 bg-slate-50 border-b border-slate-200">
          <CalendarClock className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-bold text-slate-700">Terminiert</span>
          <span className="text-xs text-slate-400">({upcoming.length})</span>
        </div>
        {upcoming.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-slate-400">Kein Angebot mit künftigem Nachfasstermin.</p>
        ) : (
          <div className="divide-y divide-slate-100">{upcoming.map((q) => <Row key={q.id} q={q} />)}</div>
        )}
      </div>

      {/* Verlauf */}
      {showHistory && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-4 bg-slate-50 border-b border-slate-200">
            <History className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-bold text-slate-700">Verlauf</span>
            <span className="text-xs text-slate-400">({history.length})</span>
          </div>
          {history.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Noch kein Nachfassen erfasst.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {history.map(({ quote: q, entry }) => (
                <div key={`${q.id}-${entry.at}`} className="px-6 py-3">
                  <div className="flex items-center gap-2 flex-wrap text-xs text-slate-400">
                    <span className="font-bold text-slate-700 text-sm">{q.quoteNumber}</span>
                    <span className="text-slate-500 text-sm truncate max-w-[220px]">{customerName(q)}</span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">{followUpOutcomeLabel(entry.outcome)}</span>
                    <span>{fmtDateTime(entry.at)}</span>
                    <span>· {entry.byName}</span>
                    <span>· {entry.nextDate ? `nächstes Nachfassen ${fmtDate(entry.nextDate)}` : 'abgeschlossen'}</span>
                    <button onClick={() => onOpen(q)} className="ml-auto text-blue-600 hover:underline font-bold">Öffnen</button>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{entry.note}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
